import axios from "axios";
import jwtDefaultConfig from "./jwtDefaultConfig";

class JwtService {
  jwtConfig = { ...jwtDefaultConfig };

  isAlreadyFetchingAccessToken = false;

  subscribers = [];

  constructor() {
    axios.interceptors.request.use(
      (config) => {
        const accessToken = this.getToken();

        if (accessToken && !config.headers.Authorization) {
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`;
        }

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        } else if (config.url.includes("update-profile")) {
          config.headers["Content-Type"] = "multipart/form-data";
        } else if (
          config.method === "post" &&
          config.url.replaceAll("/", "").endsWith("expense")
        ) {
          config.headers["Content-Type"] = "multipart/form-data";
        } else {
          config.headers["Content-Type"] = "application/json";
        }
        config.headers["Access-Control-Allow-Origin"] = "*";
        return config;
      },
      (error) => {
        Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || '';
        const url = error?.config?.url || '';
        const isTenantLockedOut =
          (status === 402 && /trial|subscription|suspended/i.test(message)) ||
          (status === 403 && /cancelled/i.test(message));
        // 401 = "Please authenticate" — token invalid or backing record (staff/user/tenant)
        // was deleted. Only kick out users who currently have an active session; otherwise
        // 401 on /auth/login (wrong credentials) would loop us.
        const isAuthRevoked =
          status === 401 &&
          (this.getIsLogin() || this.getUserIsLogin()) &&
          !/\/auth\/login|\/user-auth\/login|\/platform\/auth\/login/.test(url);

        if (isTenantLockedOut || isAuthRevoked) {
          const onCustomerSurface =
            this.getUserIsLogin() && /\/user(-|\/)/.test(window.location.pathname);

          if (onCustomerSurface) {
            this.clearUserSession();
            if (window.location.pathname !== '/user-login') {
              window.location.href = '/user-login';
            }
          } else if (this.getIsLogin()) {
            this.removeToken();
            this.removeRefreshtoken();
            this.removeUser();
            this.setIsLogin(false);
            localStorage.removeItem('tenantProfile');
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          } else if (this.getUserIsLogin()) {
            // fallback: customer session in localStorage but URL didn't match — still log them out
            this.clearUserSession();
            if (window.location.pathname !== '/user-login') {
              window.location.href = '/user-login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter((callback) =>
      callback(accessToken)
    );
  }

  addSubscriber(callback) {
    this.subscribers.push(callback);
  }

  setIsLogin(check) {
    localStorage.setItem(this.jwtConfig.storageIsLoginKeyName, check);
  }

  getIsLogin() {
    const isLogin = localStorage.getItem(this.jwtConfig.storageIsLoginKeyName);
    if (isLogin && (isLogin === true || isLogin === "true")) return true;
    else return false;
  }

  removeIsLogin() {
    localStorage.removeItem(this.jwtConfig.storageIsLoginKeyName);
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value);
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName);
  }

  removeToken() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value);
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName);
  }

  removeRefreshtoken() {
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
  }

  setUser(value) {
    localStorage.setItem(
      this.jwtConfig.storageUserKeyName,
      JSON.stringify(value)
    );
  }

  getUser() {
    return JSON.parse(localStorage.getItem(this.jwtConfig.storageUserKeyName));
  }

  removeUser() {
    localStorage.removeItem(this.jwtConfig.storageUserKeyName);
  }

  isPlatformAdmin() {
    return this.getUser()?.role === 'platform_admin';
  }

  hasPermission(permission) {
    const user = this.getUser();
    if (user?.type === 'superadmin' || user?.role === 'platform_admin') return true;
    return user?.permissions?.includes(permission) ?? false;
  }

  login(args) {
    return axios.post(this.jwtConfig.loginEndpoint, args);
  }

  signup(payload) {
    return axios.post(this.jwtConfig.signupEndpoint, payload);
  }

  verifyEmail(token) {
    return axios.post(this.jwtConfig.verifyEmailEndpoint, { token });
  }

  resendVerificationEmail(email) {
    return axios.post(this.jwtConfig.resendVerificationEndpoint, { email });
  }

  submitInquiry(payload) {
    return axios.post(this.jwtConfig.inquiryEndpoint, payload);
  }

  platformLogin(args) {
    return axios.post(this.jwtConfig.platformLoginEndpoint, args);
  }

  getAllTenants() {
    return axios.get(this.jwtConfig.tenantsEndpoint);
  }

  createTenant(payload) {
    return axios.post(this.jwtConfig.tenantsEndpoint, payload);
  }

  updateTenant(id, payload) {
    return axios.patch(`${this.jwtConfig.tenantsEndpoint}/${id}`, payload);
  }

  deleteTenant(id) {
    return axios.delete(`${this.jwtConfig.tenantsEndpoint}/${id}`);
  }

  suspendTenant(id) {
    return axios.post(`${this.jwtConfig.tenantsEndpoint}/${id}/suspend`);
  }

  activateTenant(id) {
    return axios.post(`${this.jwtConfig.tenantsEndpoint}/${id}/activate`);
  }

  getTenantStats(id) {
    return axios.get(`${this.jwtConfig.tenantsEndpoint}/${id}/stats`);
  }

  getTenantSuperadmin(id) {
    return axios.get(`${this.jwtConfig.tenantsEndpoint}/${id}/superadmin`);
  }

  recordTenantPayment(id, payload) {
    return axios.post(`${this.jwtConfig.tenantsEndpoint}/${id}/payments`, payload);
  }

  getTenantPayments(id) {
    return axios.get(`${this.jwtConfig.tenantsEndpoint}/${id}/payments`);
  }

  getMonthlyRevenue(months = 12) {
    return axios.get(`${this.jwtConfig.platformRevenueEndpoint}?months=${months}`);
  }

  getPlatformStats() {
    return axios.get(this.jwtConfig.platformStatsEndpoint);
  }

  getPublicPlans() {
    return axios.get(this.jwtConfig.publicPlanEndpoint);
  }

  getAllPlans() {
    return axios.get(this.jwtConfig.planEndpoint);
  }

  createPlan(payload) {
    return axios.post(this.jwtConfig.planEndpoint, payload);
  }

  updatePlan(key, payload) {
    return axios.patch(`${this.jwtConfig.planEndpoint}/${key}`, payload);
  }

  deactivatePlan(key) {
    return axios.post(`${this.jwtConfig.planEndpoint}/${key}/deactivate`);
  }

  reactivatePlan(key) {
    return axios.post(`${this.jwtConfig.planEndpoint}/${key}/reactivate`);
  }

  deletePlan(key) {
    return axios.delete(`${this.jwtConfig.planEndpoint}/${key}`);
  }

  updatePassword(args) {
    return axios.post(this.jwtConfig.updatePasswordEndpoint, args);
  }

  forgotPassword(args) {
    return axios.post(this.jwtConfig.forgotPasswordEndpoint, args);
  }

  resetPassword(args) {
    return axios.post(this.jwtConfig.resetPasswordEndpoint, args);
  }

  logout() {
    return axios.post(this.jwtConfig.logoutEndpoint, {
      refreshToken: this.getRefreshToken(),
    });
  }

  refreshToken() {
    return axios.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    });
  }

  createIsp(payload) {
    return axios.post(this.jwtConfig.ispEndpoint, payload);
  }

  editIsp(id, payload) {
    return axios.patch(`${this.jwtConfig.ispEndpoint}/${id}`, payload);
  }

  updateProfile(args) {
    return axios.post(`${this.jwtConfig.updateProfileEndpoint}`, args);
  }

  completeOnboarding() {
    return axios.post(`${this.jwtConfig.completeOnboardingEndpoint}`);
  }

  getAllIsps(includeDeleted = false) {
    return axios.get(this.jwtConfig.ispEndpoint, { params: includeDeleted ? { includeDeleted: true } : {} });
  }

  deleteIsp(id) {
    return axios.delete(`${this.jwtConfig.ispEndpoint}/${id}`);
  }

  restoreIsp(id) {
    return axios.patch(`${this.jwtConfig.ispEndpoint}/${id}/restore`);
  }

  getIspById(id) {
    return axios.get(`${this.jwtConfig.ispEndpoint}/${id}`);
  }

  createPackage(payload) {
    return axios.post(this.jwtConfig.packageEndpoint, payload);
  }

  getAllPackages(isp) {
    return axios.get(`${this.jwtConfig.packageEndpoint}/by-isp/${isp}`);
  }

  updatePackage(id, payload) {
    return axios.patch(`${this.jwtConfig.packageEndpoint}/${id}`, payload);
  }

  deletePackage(id) {
    return axios.delete(`${this.jwtConfig.packageEndpoint}/${isp}`);
  }

  createUser(payload) {
    return axios.post(this.jwtConfig.userEndpoint, payload);
  }

  editUser(id, payload) {
    return axios.patch(`${this.jwtConfig.userEndpoint}/${id}`, payload);
  }

  getAllUsers() {
    return axios.get(this.jwtConfig.userEndpoint);
  }

  getAutoCompleteUsers(userId) {
    return axios.post(`${this.jwtConfig.userEndpoint}/autocomplete`, {
      userId,
    });
  }

  deleteUser(id) {
    return axios.delete(`${this.jwtConfig.userEndpoint}/${id}`);
  }

  downloadUserImportTemplate() {
    return axios.get(this.jwtConfig.userImportTemplateEndpoint, { responseType: "blob" });
  }

  previewUserImport(file) {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(this.jwtConfig.userImportPreviewEndpoint, formData);
  }

  commitUserImport(file) {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(this.jwtConfig.userImportCommitEndpoint, formData);
  }

  downloadEntryImportTemplate() {
    return axios.get(this.jwtConfig.entryImportTemplateEndpoint, { responseType: "blob" });
  }

  previewEntryImport(file, ispId) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("ispId", ispId);
    return axios.post(this.jwtConfig.entryImportPreviewEndpoint, formData);
  }

  commitEntryImport(file, ispId) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("ispId", ispId);
    return axios.post(this.jwtConfig.entryImportCommitEndpoint, formData);
  }

  createEntry(payload) {
    return axios.post(this.jwtConfig.entryEndpoint, payload);
  }

  updateEntry(id, payload) {
    return axios.patch(`${this.jwtConfig.entryEndpoint}/${id}`, payload);
  }

  deleteEntry(id) {
    return axios.delete(`${this.jwtConfig.entryEndpoint}/${id}`);
  }

  getEntryById(id) {
    return axios.get(`${this.jwtConfig.entryEndpoint}/${id}`);
  }

  getAllCompletedEntries(payload) {
    return axios.post(`${this.jwtConfig.entryEndpoint}/completed`, payload);
  }

  getAllPendingEntries() {
    return axios.get(`${this.jwtConfig.entryEndpoint}/pending`);
  }

  getAllPendingEntriesWithinDateRange(payload) {
    return axios.post(`${this.jwtConfig.entryEndpoint}/pending`, payload);
  }

  getEntriesByUserId(userId) {
    return axios.get(`${this.jwtConfig.entryEndpoint}/user/${userId}`);
  }

  searchEntriesByTid(tid) {
    return axios.get(`${this.jwtConfig.entryEndpoint}/search-by-tid?tid=${encodeURIComponent(tid)}`);
  }

  getExpiringEntries() {
    return axios.get(`${this.jwtConfig.entryEndpoint}/expiring`);
  }

  addStaff(payload) {
    return axios.post(this.jwtConfig.staffEndpoint, payload);
  }

  updateStaff(id, payload) {
    return axios.patch(`${this.jwtConfig.staffEndpoint}/${id}`, payload);
  }

  deleteStaff(id) {
    return axios.delete(`${this.jwtConfig.staffEndpoint}/${id}`);
  }

  getAllStaffs() {
    return axios.get(this.jwtConfig.staffEndpoint);
  }

  createInvoice(payload) {
    return axios.post(this.jwtConfig.invoiceEndpoint, payload);
  }

  getSentInvoices(payload) {
    return axios.post(
      `${this.jwtConfig.invoiceEndpoint}/sent-invoices`,
      payload
    );
  }

  getAllInvoices(payload) {
    return axios.post(
      `${this.jwtConfig.invoiceEndpoint}/all-invoices`,
      payload
    );
  }

  updateInvoice(id, payload) {
    return axios.patch(`${this.jwtConfig.invoiceEndpoint}/${id}`, payload);
  }

  deleteInvoice(id) {
    return axios.delete(`${this.jwtConfig.invoiceEndpoint}/${id}`);
  }

  createExpense(payload) {
    return axios.post(this.jwtConfig.expenseEndpoint, payload);
  }

  getCompletedExpenses(payload) {
    return axios.post(`${this.jwtConfig.expenseEndpoint}/completed`, payload);
  }

  getPendingExpenses(payload) {
    return axios.post(`${this.jwtConfig.expenseEndpoint}/pending`, payload);
  }

  approveExpense(id) {
    return axios.get(`${this.jwtConfig.expenseEndpoint}/approve/${id}`);
  }

  declineExpense(id) {
    return axios.get(`${this.jwtConfig.expenseEndpoint}/decline/${id}`);
  }

  deleteExpense(id) {
    return axios.delete(`${this.jwtConfig.expenseEndpoint}/${id}`);
  }

  reverseExpense(id) {
    return axios.get(`${this.jwtConfig.expenseEndpoint}/reverse/${id}`);
  }

  reviveExpense(id) {
    return axios.get(`${this.jwtConfig.expenseEndpoint}/revive/${id}`);
  }

  getSummary(payload) {
    return axios.post(this.jwtConfig.summaryEndpoint, payload);
  }


  getSummaryTrend({ year, anchorMonth, months }) {
    return axios.get(`${this.jwtConfig.summaryEndpoint}/trend`, {
      params: { year, anchorMonth, months },
    });
  }

  sendServerDownAlert(id) {
    return axios.get(`${this.jwtConfig.ispEndpoint}/server-down-alert/${id}`);
  }

  sendServerUpAlert(id) {
    return axios.get(`${this.jwtConfig.ispEndpoint}/server-up-alert/${id}`);
  }

  sendExpiryAlert() {
    return axios.get(`${this.jwtConfig.summaryEndpoint}/send-expiry-alert`);
  }

  createExtraIncome(payload) {
    return axios.post(this.jwtConfig.extraIncomeEndpoint, payload);
  }

  getAllCompletedExtraIncome(payload) {
    return axios.post(
      `${this.jwtConfig.extraIncomeEndpoint}/completed`,
      payload
    );
  }

  getAllPendingExtraIncome(payload) {
    return axios.post(`${this.jwtConfig.extraIncomeEndpoint}/pending`, payload);
  }

  updateExtraIncome(id, payload) {
    return axios.patch(`${this.jwtConfig.extraIncomeEndpoint}/${id}`, payload);
  }

  deleteExtraIncome(id) {
    return axios.delete(`${this.jwtConfig.extraIncomeEndpoint}/${id}`);
  }

  getTenantProfile() {
    return axios.get(`${this.jwtConfig.settingsEndpoint}/profile`);
  }

  uploadTenantLogo(formData) {
    return axios.post(`${this.jwtConfig.settingsEndpoint}/logo`, formData);
  }

  updateTenantProfile(data) {
    return axios.put(`${this.jwtConfig.settingsEndpoint}/profile`, data);
  }

  // ---- Notifications (staff) ----
  getNotifications(params) {
    return axios.get(this.jwtConfig.notificationEndpoint, { params });
  }
  getUnreadNotificationCount() {
    return axios.get(`${this.jwtConfig.notificationEndpoint}/unread-count`);
  }
  markNotificationRead(id) {
    return axios.patch(`${this.jwtConfig.notificationEndpoint}/${id}/read`);
  }
  markAllNotificationsRead() {
    return axios.patch(`${this.jwtConfig.notificationEndpoint}/read-all`);
  }

  getWhatsappSettings() {
    return axios.get(`${this.jwtConfig.settingsEndpoint}/whatsapp`);
  }

  updateWhatsappSettings(data) {
    return axios.put(`${this.jwtConfig.settingsEndpoint}/whatsapp`, data);
  }

  testWhatsappMessage(phone) {
    return axios.post(`${this.jwtConfig.settingsEndpoint}/whatsapp/test`, { phone });
  }

  getAuditLogs(payload) {
    return axios.post(this.jwtConfig.auditLogEndpoint, payload);
  }

  setCustomerPassword(id, data) {
    return axios.patch(`${this.jwtConfig.userEndpoint}/${id}/set-password`, data);
  }

  // ---- User (customer) session helpers ----
  getUserAccessToken() { return localStorage.getItem(this.jwtConfig.storageUserAccessTokenKey); }
  setUserAccessToken(token) { localStorage.setItem(this.jwtConfig.storageUserAccessTokenKey, token); }
  getUserInfo() { return JSON.parse(localStorage.getItem(this.jwtConfig.storageUserInfoKey) || 'null'); }
  setUserInfo(info) { localStorage.setItem(this.jwtConfig.storageUserInfoKey, JSON.stringify(info)); }
  getUserIsLogin() { return localStorage.getItem(this.jwtConfig.storageUserIsLoginKey) === 'true'; }
  setUserIsLogin(v) { localStorage.setItem(this.jwtConfig.storageUserIsLoginKey, String(v)); }
  clearUserSession() {
    localStorage.removeItem(this.jwtConfig.storageUserAccessTokenKey);
    localStorage.removeItem(this.jwtConfig.storageUserRefreshTokenKey);
    localStorage.removeItem(this.jwtConfig.storageUserInfoKey);
    localStorage.removeItem(this.jwtConfig.storageUserIsLoginKey);
  }

  // ---- User auth API calls (use userAccessToken header) ----
  userLogin(payload) {
    return axios.post(this.jwtConfig.userLoginEndpoint, payload);
  }
  userForgotPassword(payload) {
    return axios.post(this.jwtConfig.userForgotPasswordEndpoint, payload);
  }
  userResetPassword(payload) {
    return axios.post(this.jwtConfig.userResetPasswordEndpoint, payload);
  }

  // ---- Customer data (needs userAccessToken) ----
  getMyEntries() {
    return axios.get(this.jwtConfig.customerEntriesEndpoint, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  getMyTenant() {
    return axios.get(this.jwtConfig.customerTenantEndpoint, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  getMyProfile() {
    return axios.get(this.jwtConfig.customerProfileEndpoint, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  updateMyProfile(formData) {
    return axios.post(this.jwtConfig.customerUpdateProfileEndpoint, formData, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  updateMyPassword(payload) {
    return axios.post(this.jwtConfig.customerUpdatePasswordEndpoint, payload, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  getMyComplaints() {
    return axios.get(`${this.jwtConfig.complaintEndpoint}/my`, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }
  createComplaint(payload) {
    return axios.post(this.jwtConfig.complaintEndpoint, payload, {
      headers: { Authorization: `Bearer ${this.getUserAccessToken()}` },
    });
  }

  // ---- Staff complaint management ----
  getAllComplaints(params) {
    return axios.get(this.jwtConfig.complaintEndpoint, { params });
  }
  resolveComplaint(id, payload) {
    return axios.patch(`${this.jwtConfig.complaintEndpoint}/${id}/resolve`, payload);
  }
  updateComplaintStatus(id, payload) {
    return axios.patch(`${this.jwtConfig.complaintEndpoint}/${id}/status`, payload);
  }
}

const jwt = new JwtService();
export default jwt;
